import type { FC } from "react";

import type { StoryFlowLockRecord } from "../../types/storyFlowLocks";

import { formatUsd } from "../../utils/currency";
import {
  type FlowCategory,
  FLOW_CATEGORY_CONFIG,
} from "../../utils/taskFlowGrouping";
import { STATUS_DOT_CONFIGS } from "../../utils/taskStatus";
import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { DataTable } from "../common/DataTable";
import { HudActionButton } from "../common/HudActionButton";
import { ChevronRightIcon } from "../common/icons";
import { RepoActionPopover } from "../common/RepoActionPopover";
import { Text } from "../common/Text";

interface FlowGroupHeaderProps {
  category: FlowCategory;
  ticketId: string | null;
  taskCount: number;
  totalCostUsd: number;
  statusCounts: Record<string, number>;
  latestUpdatedAt: number;
  collapsed: boolean;
  onToggle: () => void;
  lock?: StoryFlowLockRecord | null;
  onViewFlow?: () => void;
  repositorySlugs?: string[];
  referenceRepositorySlugs?: string[];
  onNavigateToIndexing?: (repoSlug: string) => void;
}

const FlowLockIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const ViewFlowIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const FlowStatusSummary: FC<{ statusCounts: Record<string, number> }> = ({
  statusCounts,
}) => (
  <span className="flex items-center gap-2">
    {STATUS_DOT_CONFIGS.filter((s) => (statusCounts[s.key] ?? 0) > 0).map(
      (s) => (
        <span
          key={s.key}
          className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400"
          title={`${statusCounts[s.key]} ${s.label}`}
        >
          {s.pulse ? (
            <ActivePulseIndicator
              size="xs"
              colorClassName="bg-sky-500 dark:bg-sky-400"
              radarClassName="border-sky-500/50 dark:border-sky-400/50"
            />
          ) : (
            <span className={`h-1.5 w-1.5 rounded-none ${s.color}`} />
          )}
          {statusCounts[s.key]}
        </span>
      ),
    )}
  </span>
);

const RepoSlugPill: FC<{
  slug: string;
  role: "primary" | "reference";
  onIndex?: () => void;
}> = ({ slug, role, onIndex }) => {
  const shortSlug = slug.includes("/") ? slug.split("/").pop()! : slug;
  const pill = (
    <span
      data-repo-pill
      className="inline-flex items-center gap-1 whitespace-nowrap border border-slate-200 bg-slate-50 px-1.5 py-px font-mono text-[9px] tracking-[0.14em] text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400"
    >
      <span
        className={`h-1 w-1 rounded-full ${
          role === "primary"
            ? "bg-slate-600 dark:bg-slate-300"
            : "border border-slate-400 bg-transparent dark:border-slate-500"
        }`}
      />
      {shortSlug}
    </span>
  );

  return (
    <RepoActionPopover
      side="bottom"
      info={
        <div className="space-y-0.5">
          <div>{slug}</div>
          <div className="text-slate-400 dark:text-slate-500">
            {role === "primary" ? "Modifiable" : "Reference"}
          </div>
        </div>
      }
      actions={onIndex ? [{ label: "Index", onClick: onIndex }] : []}
    >
      {pill}
    </RepoActionPopover>
  );
};

export const FlowGroupHeader: FC<FlowGroupHeaderProps> = ({
  category,
  ticketId,
  taskCount,
  totalCostUsd,
  statusCounts,
  latestUpdatedAt,
  collapsed,
  onToggle,
  lock,
  onViewFlow,
  repositorySlugs = [],
  referenceRepositorySlugs = [],
  onNavigateToIndexing,
}) => {
  const config = FLOW_CATEGORY_CONFIG[category];
  const hasCost = totalCostUsd > 0;

  return (
    <DataTable.Row
      className="cursor-pointer bg-slate-50/70 dark:bg-slate-950/30"
      style={{ boxShadow: config.accentShadow }}
      onClick={onToggle}
    >
      <DataTable.Cell colSpan={7}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-x-3">
          {/* Left: icon + ticket + badge + repos — all inline so a lone pill
              doesn't orphan on its own row. */}
          <div
            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1"
            onClick={(e) => {
              // Allow pill popovers to capture their own clicks without
              // Toggling the group.
              const { target } = e;
              if (
                target instanceof Element &&
                target.closest("[data-repo-pill]")
              ) {
                e.stopPropagation();
              }
            }}
          >
            <svg
              className={`h-4 w-4 flex-shrink-0 ${config.iconClass}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d={config.iconPath}
              />
            </svg>
            {ticketId && (
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-800 dark:text-slate-200">
                {ticketId}
              </span>
            )}
            <span
              className={`inline-flex flex-shrink-0 items-center border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${config.badgeClass}`}
            >
              {config.label}
            </span>
            {lock?.lockOwner && (
              <span
                className="inline-flex flex-shrink-0 items-center gap-1 border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                title={`Locked by ${lock.lockOwner}${lock.lockExpiresAt ? ` · expires ${new Date(lock.lockExpiresAt).toLocaleTimeString()}` : ""}`}
              >
                <FlowLockIcon />
                {lock.lockOwner}
              </span>
            )}
            {repositorySlugs.map((slug) => (
              <RepoSlugPill
                key={`primary-${slug}`}
                slug={slug}
                role="primary"
                onIndex={
                  onNavigateToIndexing
                    ? () => onNavigateToIndexing(slug)
                    : undefined
                }
              />
            ))}
            {referenceRepositorySlugs.map((slug) => (
              <RepoSlugPill
                key={`ref-${slug}`}
                slug={slug}
                role="reference"
                onIndex={
                  onNavigateToIndexing
                    ? () => onNavigateToIndexing(slug)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Right: count + status summary + cost + chevron */}
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
            <Text className="whitespace-nowrap">
              {taskCount} {taskCount === 1 ? "task" : "tasks"}
            </Text>
            <FlowStatusSummary statusCounts={statusCounts} />
            <Text className="hidden whitespace-nowrap sm:inline">
              {formatRelativeTime(latestUpdatedAt)}
            </Text>
            {hasCost && (
              <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                {formatUsd(totalCostUsd)}
              </span>
            )}
            {onViewFlow && (
              <HudActionButton
                label="View"
                tone="neutral"
                leftIcon={<ViewFlowIcon />}
                title="View flow details"
                className="!px-2 !py-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewFlow();
                }}
              />
            )}
            <ChevronRightIcon
              size={16}
              className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${collapsed ? "" : "rotate-90"}`}
            />
          </div>
        </div>
      </DataTable.Cell>
    </DataTable.Row>
  );
};
